import React, { useState } from "react";
import { db } from "../../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { FileText } from "lucide-react";
import TagList from "./TagList";

interface FormProps {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export default function ApplicationForm({ onSuccess, onError }: FormProps) {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const universityOptions = [
    "APU",
    "Curtin",
    "Help",
    "IGlobal",
    "Manipal",
    "MIA",
    "QIU",
    "Segi",
    "YTL",
    "UOC",
    "UOW",
    "PSB",
    "Kaplan",
    "Amity",
    "EAIM",
    "MDIS",
    "Teesside",
    "UOSW",
    "UOS",
    "UWE",
    "UOH",
    "UON",
    "Coventry",
  ];
  const [university, setLocalUni] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    let newErrors: { [key: string]: string } = {};

    const name = (formData.get("name") as string).trim();
    const phone = (formData.get("phone-number") as string).trim();
    const email = (formData.get("email") as string).trim();

    // ✅ Validate inputs
    if (!/^[A-Za-z]+( [A-Za-z]+)*$/.test(name)) {
      newErrors.name = "Name must only contain letters and spaces.";
    }
    if (!/^(\+?60|0)1\d{8,9}$/.test(phone)) {
      newErrors.phone = "Invalid Malaysian phone number.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email address.";
    }

    const file = formData.get("resume") as File;
    if (!file) {
      newErrors.resume = "Please attach your resume (PDF only).";
    } else if (file.type !== "application/pdf") {
      newErrors.resume = "Only PDF files are allowed.";
    } else if (file.size > 5 * 1024 * 1024) {
      newErrors.resume = "File size must not exceed 5MB.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const localUnis = university;

      const position = formData.get("position") as string;
      const experience = formData.get("experience") as string;
      const location = formData.get("location") as string;

      // ✅ Step 1: Upload the resume to Firebase Storage
      const storage = getStorage();
      const fileRef = ref(storage, `resumes/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const fileURL = await getDownloadURL(fileRef);

      // ✅ Step 2: Save form data + resume URL in Firestore
      const candidateData = {
        name,
        phoneNumber: phone,
        email,
        localUniversity: localUnis,
        position,
        location,
        experiences: experience,
        status: "Pending",
        resumeURL: fileURL,
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "job-application"), candidateData);

      form.reset();
      setSelectedFileName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (onSuccess)
        onSuccess(
          "Your information and resume have been successfully submitted!"
        );
    } catch (err) {
      console.error("Error submitting form:", err);
      const errorMsg =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      if (onError) onError(`Failed to submit: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      className="w-full relative p-1.5 lg:p-2 overflow-hidden"
      onSubmit={handleSubmit}
    >
      <div className="absolute inset-0 bg-linear-to-r from-[#FF61D2] to-[#FE9090] animate-pulse duration-300 rounded"></div>

      <div className="grid grid-cols-1 gap-x-6 gap-y-6 p-4 bg-white relative rounded">
        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Full Name
          </label>
          <input
            id="name"
            name="name"
            required
            type="text"
            className="block w-full rounded-md border px-3 py-1.5"
          />
          {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
        </div>

        {/* Phone Number */}
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Phone Number
          </label>
          <input
            id="phone-number"
            name="phone-number"
            required
            type="text"
            placeholder="0123456789"
            className="block w-full rounded-md border px-3 py-1.5"
          />
          {errors.phone && (
            <p className="text-red-500 text-sm">{errors.phone}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Email Address
          </label>
          <input
            id="email"
            name="email"
            required
            type="email"
            className="block w-full rounded-md border px-3 py-1.5"
          />
          {errors.email && (
            <p className="text-red-500 text-sm">{errors.email}</p>
          )}
        </div>

        {/* Local University */}
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Preferred University
          </label>
          {/* <select
            id="qualification"
            name="qualification"
            required
            defaultValue=""
            className="block w-full rounded-md border px-3 py-1.5"
          >
            <option value="" disabled>
              Please select
            </option>
            <option>SPM</option>
            <option>Diploma</option>
            <option>Degree</option>
            <option>Master</option>
            <option>Others</option>
          </select> */}
          <select
            value=""
            onChange={(e) => {
              const val = e.target.value;
              if (val && !university.includes(val)) {
                setLocalUni([...university, val]);
              }
            }}
            className="block w-full rounded-md border px-3 py-1.5"
          >
            <option value="">Select at least one university</option>
            {universityOptions
              .filter((opt) => !university.includes(opt))
              .map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
          </select>

          {/* Tag Display & Delete */}
          {university.length > 0 && (
            <TagList tags={university} onChange={setLocalUni} />
          )}
        </div>

        {/* Position */}
        {/* <div>
          <label className="block text-sm font-medium text-gray-900">
            Interested Job Position
          </label>
          <select
            id="position"
            name="position"
            required
            defaultValue=""
            className="block w-full rounded-md border px-3 py-1.5"
          >
            <option value="" disabled>
              Please select
            </option>
            <option>Sales Education Consultant</option>
            <option>Marketing Executive</option>
          </select>
        </div> */}

        {/* Location */}
        {/* <div>
          <label className="block text-sm font-medium text-gray-900">
            Preferred Working Location
          </label>
          <select
            id="location"
            name="location"
            required
            defaultValue=""
            className="block w-full rounded-md border px-3 py-1.5"
          >
            <option value="" disabled>
              Please select
            </option>
            <option>Kota Damansara</option>
            <option>Kuantan</option>
          </select>
        </div> */}

        {/* Experience */}
        {/* <div>
          <label className="block text-sm font-medium text-gray-900">
            Do you have any experience in sales?
          </label>
          <select
            id="experience"
            name="experience"
            required
            defaultValue=""
            className="block w-full rounded-md border px-3 py-1.5"
          >
            <option value="" disabled>
              Please select
            </option>
            <option>Yes</option>
            <option>No</option>
          </select>
        </div> */}

        {/* Resume Upload */}
        <div>
          {/* <label className="block text-sm font-medium text-gray-900 mb-1">
            Attach Your Resume (PDF only)
            {errors.resume && (
              <p className="text-red-500 text-sm">{errors.resume}</p>
            )}
          </label> */}

          {/* Custom File Input */}
          {/* <label className="flex items-center justify-center w-full cursor-pointer border rounded-md py-2 px-3 bg-gray-50 hover:bg-blue-100">
            <span className=" text-sm">
              {selectedFileName ? "Change File" : "Choose File"}
            </span>
            <input
              ref={fileInputRef}
              id="resume"
              name="resume"
              type="file"
              accept="application/pdf"
              required
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedFileName(file.name);
                } else {
                  setSelectedFileName("");
                }
              }}
            />
          </label> */}

          {/* Display selected filename or default message */}
          {/* <p className="text-sm text-gray-600 mt-1 text-center">
            {selectedFileName ? (
              <span className="flex items-center justify-center gap-1">
                <FileText className="w-5 h-5" /> {selectedFileName}
              </span>
            ) : (
              <span className="italic text-gray-400">No file chosen</span>
            )}
          </p> */}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded col-span-full disabled:opacity-50 cursor-pointer"
        >
          {loading ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  );
}
